import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Search, Loader2, ShoppingCart, ChevronDown, BookOpen, Brain, Wrench, Package, AlertTriangle } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { toast } from "sonner";
import symptomBoard from "@/assets/symptom-explorer-board.jpg";
import { fetchProductByHandle } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import ClinicalSourcesBadge from "@/components/ClinicalSourcesBadge";
import { categoryIcon, iconForEmoji } from "@/components/ui/IconMap";

interface Symptom {
  id: string;
  title: string;
  category: string;
  icon: string;
  slug: string;
  science_content: string;
  excerpt: string;
  sources?: unknown;
  clinical_grade?: string | null;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
  next_review_at?: string | null;
}

interface QuizQuestion {
  id: string;
  question: string;
  options: any;
  correct_index: number;
  explanation: string;
  order_index: number;
}

interface Tool {
  id: string;
  title: string;
  description: string;
  icon: string;
  order_index: number;
}

interface Product {
  id: string;
  name: string;
  description: string;
  link: string;
  image_url: string;
  order_index: number;
  shopify_product_handle?: string;
}

const CATEGORIES = [
  { value: 'all', label: 'All Symptoms' },
  { value: 'physical', label: 'Physical' },
  { value: 'mental_emotional', label: 'Mental/Emotional' },
  { value: 'urogenital', label: 'Urogenital' },
  { value: 'sleep', label: 'Sleep' },
  { value: 'cognitive', label: 'Cognitive' },
  { value: 'cardiovascular', label: 'Cardiovascular' },
  { value: 'metabolic', label: 'Metabolic' },
];

const getCategoryColor = (_category: string) => {
  // Editorial: no candy gradients. Warm neutral card surface across all categories.
  return 'bg-card';
};


export default function SymptomExplorer() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [symptoms, setSymptoms] = useState<Symptom[]>([]);
  const [selectedSymptom, setSelectedSymptom] = useState<Symptom | null>(null);
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [quizAnswers, setQuizAnswers] = useState<{ [key: string]: number }>({});
  const [showResults, setShowResults] = useState(false);
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const { addItem } = useCartStore();

  useEffect(() => {
    loadSymptoms();
    
    // Safety timeout - if loading takes more than 10 seconds, force it to false
    const timeoutId = setTimeout(() => {
      console.warn('Loading timeout reached, forcing loading state to false');
      setLoading(false);
    }, 10000);
    
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (slug && symptoms.length > 0) {
      const symptom = symptoms.find(s => s.slug === slug);
      if (symptom) {
        handleSymptomClick(symptom);
      }
    }
  }, [slug, symptoms]);

  const loadSymptoms = async () => {
    try {
      console.log('Loading symptoms...', new Date().toISOString());
      setLoading(true);
      
      const { data, error } = await supabase
        .from('symptom_library')
        .select('*')
        .eq('is_published', true)
        .order('title');

      if (error) {
        console.error('Symptom load error:', error);
        toast.error(`Failed to load symptoms: ${error.message}`);
        setSymptoms([]);
        return;
      }
      
      console.log('Symptoms loaded successfully:', data?.length, 'symptoms', new Date().toISOString());
      setSymptoms(data || []);
    } catch (error: any) {
      console.error('Load symptoms catch:', error);
      toast.error('An unexpected error occurred while loading symptoms');
      setSymptoms([]);
    } finally {
      console.log('Setting loading to false', new Date().toISOString());
      setLoading(false);
    }
  };

  const loadSymptomDetails = async (symptomId: string) => {
    try {
      setLoading(true);
      console.log('Loading details for symptom:', symptomId);
      const [quizRes, toolsRes, productsRes] = await Promise.all([
        supabase.from('symptom_quiz_questions').select('*').eq('symptom_id', symptomId).order('order_index'),
        supabase.from('symptom_tools').select('*').eq('symptom_id', symptomId).order('order_index'),
        supabase.from('symptom_products').select('*').eq('symptom_id', symptomId).order('order_index'),
      ]);

      if (quizRes.error) {
        console.error('Quiz error:', quizRes.error);
        throw quizRes.error;
      }
      if (toolsRes.error) {
        console.error('Tools error:', toolsRes.error);
        throw toolsRes.error;
      }
      if (productsRes.error) {
        console.error('Products error:', productsRes.error);
        throw productsRes.error;
      }

      console.log('Loaded quiz questions:', quizRes.data);
      console.log('Loaded tools:', toolsRes.data);
      console.log('Loaded products:', productsRes.data);

      setQuizQuestions(quizRes.data || []);
      setTools(toolsRes.data || []);
      setProducts(productsRes.data || []);
      setQuizAnswers({});
      setShowResults(false);
    } catch (error: any) {
      toast.error('Failed to load symptom details');
      console.error('Load details catch:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSymptomClick = (symptom: Symptom) => {
    setSelectedSymptom(symptom);
    loadSymptomDetails(symptom.id);
    navigate(`/symptom-explorer/${symptom.slug}`);
  };

  const handleBack = () => {
    setSelectedSymptom(null);
    setQuizQuestions([]);
    setTools([]);
    setProducts([]);
    navigate('/symptom-explorer');
  };

  const handleQuizAnswer = (questionId: string, answerIndex: number) => {
    setQuizAnswers(prev => ({ ...prev, [questionId]: answerIndex }));
  };

  const getQuizScore = () => {
    let correct = 0;
    quizQuestions.forEach(q => {
      if (quizAnswers[q.id] === q.correct_index) correct++;
    });
    return { correct, total: quizQuestions.length };
  };

  const filteredSymptoms = symptoms.filter(symptom => {
    const matchesSearch = symptom.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         symptom.excerpt?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || symptom.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const groupedSymptoms = CATEGORIES.slice(1).reduce((acc, cat) => {
    acc[cat.value] = filteredSymptoms.filter(s => s.category === cat.value);
    return acc;
  }, {} as Record<string, Symptom[]>);

  const handleAddToCart = async (product: Product) => {
    if (!product.shopify_product_handle) {
      toast.error("This product is not available for purchase");
      return;
    }

    setAddingToCart(product.id);
    try {
      const shopifyProduct = await fetchProductByHandle(product.shopify_product_handle);
      
      if (!shopifyProduct || !shopifyProduct.node.variants.edges.length) {
        toast.error("Product not found or out of stock");
        return;
      }

      const firstVariant = shopifyProduct.node.variants.edges[0].node;
      
      if (!firstVariant.availableForSale) {
        toast.error("This product is currently out of stock");
        return;
      }

      addItem({
        product: shopifyProduct,
        variantId: firstVariant.id,
        variantTitle: firstVariant.title,
        price: firstVariant.price,
        quantity: 1,
        selectedOptions: firstVariant.selectedOptions,
      });

      toast.success("Added to cart!", {
        description: `${product.name} has been added to your cart`,
      });
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add product to cart");
    } finally {
      setAddingToCart(null);
    }
  };

  if (selectedSymptom) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background via-background to-secondary/5">
        <div className="container mx-auto px-4 py-8">
          <div className="flex gap-2 mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Home
            </Button>
            <Button
              variant="ghost"
              onClick={handleBack}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Symptom Explorer
            </Button>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-16 h-16 rounded-lg bg-muted/50 flex items-center justify-center border border-foreground/10 shrink-0">
                {(() => {
                  const Icon = iconForEmoji(selectedSymptom.icon) || categoryIcon(selectedSymptom.category);
                  return <Icon className="h-7 w-7 text-primary/80" />;
                })()}
              </div>

              <div className="flex-1">
                <h1 className="text-4xl font-bold mb-2">{selectedSymptom.title}</h1>
                <Badge variant="secondary" className="text-sm">
                  {CATEGORIES.find(c => c.value === selectedSymptom.category)?.label}
                </Badge>
              </div>
            </div>

            <div className="mb-6">
              <ClinicalSourcesBadge
                sources={selectedSymptom.sources}
                clinical_grade={selectedSymptom.clinical_grade}
                reviewed_at={selectedSymptom.reviewed_at}
                reviewed_by={selectedSymptom.reviewed_by}
                next_review_at={selectedSymptom.next_review_at}
              />
            </div>

            <Tabs defaultValue="science" className="w-full">
              <TabsList className="mb-10 flex w-full flex-wrap items-center justify-center gap-1 border-b border-border/60 bg-transparent p-0">
                <TabsTrigger
                  value="science"
                  className="group relative gap-2 rounded-none px-5 py-3 font-serif text-base font-medium text-muted-foreground transition-all hover:text-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                >
                  <BookOpen className="h-4 w-4 text-primary/70 group-data-[state=active]:text-primary" />
                  <span>The Science</span>
                  <span className="absolute bottom-0 left-0 h-[2px] w-full origin-left scale-x-0 bg-primary transition-transform duration-300 group-data-[state=active]:scale-x-100" />
                </TabsTrigger>
                <TabsTrigger
                  value="quiz"
                  className="group relative gap-2 rounded-none px-5 py-3 font-serif text-base font-medium text-muted-foreground transition-all hover:text-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                >
                  <Brain className="h-4 w-4 text-primary/70 group-data-[state=active]:text-primary" />
                  <span>Quizzes</span>
                  <span className="absolute bottom-0 left-0 h-[2px] w-full origin-left scale-x-0 bg-primary transition-transform duration-300 group-data-[state=active]:scale-x-100" />
                </TabsTrigger>
                <TabsTrigger
                  value="tools"
                  className="group relative gap-2 rounded-none px-5 py-3 font-serif text-base font-medium text-muted-foreground transition-all hover:text-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                >
                  <Wrench className="h-4 w-4 text-primary/70 group-data-[state=active]:text-primary" />
                  <span>Tools</span>
                  <span className="absolute bottom-0 left-0 h-[2px] w-full origin-left scale-x-0 bg-primary transition-transform duration-300 group-data-[state=active]:scale-x-100" />
                </TabsTrigger>
                <TabsTrigger
                  value="products"
                  className="group relative gap-2 rounded-none px-5 py-3 font-serif text-base font-medium text-muted-foreground transition-all hover:text-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                >
                  <Package className="h-4 w-4 text-primary/70 group-data-[state=active]:text-primary" />
                  <span>Products</span>
                  <span className="absolute bottom-0 left-0 h-[2px] w-full origin-left scale-x-0 bg-primary transition-transform duration-300 group-data-[state=active]:scale-x-100" />
                </TabsTrigger>
              </TabsList>

              <TabsContent value="science">
                <Card className="bg-card/50 border-border/50">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-2xl font-semibold tracking-tight">Understanding {selectedSymptom.title}</CardTitle>
                    <CardDescription className="text-base">The science behind what's happening in your body</CardDescription>
                  </CardHeader>
                  <CardContent className="bg-muted/30 rounded-b-lg p-8">
                    <div className="space-y-6">
                      {selectedSymptom.science_content
                        ?.split(/\n\n+|\n/)
                        .filter(p => p.trim().length > 0)
                        .map((paragraph, index) => (
                          <p key={index} className="text-base leading-[1.8] text-foreground/90">
                            {paragraph.trim()}
                          </p>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="quiz">
                <Card>
                  <CardHeader>
                    <CardTitle>Test Your Knowledge</CardTitle>
                    <CardDescription>See how much you know about {selectedSymptom.title}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {loading ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : quizQuestions.length > 0 ? (
                      <>
                        {quizQuestions.map((question, qIndex) => {
                          const options = Array.isArray(question.options) ? question.options : [];
                          return (
                            <div key={question.id} className="space-y-4">
                              <h3 className="font-semibold text-lg">
                                {qIndex + 1}. {question.question}
                              </h3>
                              <div className="space-y-2">
                                {options.map((option: string, oIndex: number) => (
                                  <Button
                                    key={oIndex}
                                    variant={
                                      showResults
                                        ? oIndex === question.correct_index
                                          ? "default"
                                          : quizAnswers[question.id] === oIndex
                                          ? "destructive"
                                          : "outline"
                                        : quizAnswers[question.id] === oIndex
                                        ? "secondary"
                                        : "outline"
                                    }
                                    className="w-full justify-start text-left h-auto py-3 px-4"
                                    onClick={() => handleQuizAnswer(question.id, oIndex)}
                                    disabled={showResults}
                                  >
                                    {option}
                                  </Button>
                                ))}
                              </div>
                              {showResults && (
                                <div className="mt-3 p-4 bg-secondary/20 rounded-md border">
                                  <p className={`text-sm font-medium mb-1 ${quizAnswers[question.id] === question.correct_index ? 'text-green-600' : 'text-red-600'}`}>
                                    {quizAnswers[question.id] === question.correct_index ? 'Correct' : 'Incorrect'}
                                  </p>
                                  <p className="text-sm">{question.explanation}</p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {!showResults && Object.keys(quizAnswers).length === quizQuestions.length && (
                          <Button onClick={() => setShowResults(true)} className="w-full" size="lg">
                            Check Answers
                          </Button>
                        )}
                        {showResults && (
                          <div className="p-6 bg-gradient-to-r from-primary/10 to-primary/20 rounded-lg text-center border-2 border-primary/20">
                            <p className="text-2xl font-bold mb-2">
                              Your Score: {getQuizScore().correct} / {getQuizScore().total}
                            </p>
                            <Progress 
                              value={(getQuizScore().correct / getQuizScore().total) * 100} 
                              className="h-3 mb-3"
                            />
                            <p className="text-muted-foreground">
                              {getQuizScore().correct === getQuizScore().total 
                                ? "Perfect score. You clearly know this territory well."
                                : getQuizScore().correct >= getQuizScore().total * 0.7
                                ? "Solid work. You're building real understanding."
                                : "Keep going — every question is a step toward clarity."}
                            </p>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-muted-foreground">No quiz questions available yet for this symptom.</p>
                        <p className="text-sm text-muted-foreground mt-2">Check back soon!</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="tools">
                <div className="grid gap-4">
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : tools.length > 0 ? (
                    tools.map(tool => (
                    <Card key={tool.id}>
                        <CardHeader>
                          <div className="flex items-center gap-3">
                            {(() => {
                              const Icon = iconForEmoji(tool.icon);
                              return <Icon className="h-6 w-6 text-primary/80" />;
                            })()}
                            <CardTitle>{tool.title}</CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="whitespace-pre-wrap leading-relaxed">{tool.description}</p>
                          {tool.title.toLowerCase().includes("vagal") && (
                            <Alert variant="destructive" className="mt-4">
                              <AlertTriangle className="h-4 w-4" />
                              <AlertTitle>Safety Warning</AlertTitle>
                              <AlertDescription>
                                When to get help: Seek emergency medical care immediately if your rapid heartbeat or palpitations come with severe chest pain, shortness of breath, dizziness, or fainting.
                              </AlertDescription>
                            </Alert>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <p className="text-muted-foreground">No tools available yet for this symptom.</p>
                        <p className="text-sm text-muted-foreground mt-2">Check back soon!</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="products">
                <div className="grid gap-4 md:grid-cols-2">
                  {loading ? (
                    <div className="flex justify-center py-8 md:col-span-2">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : products.length > 0 ? (
                    products.map(product => (
                      <Card key={product.id}>
                        <CardHeader>
                          <CardTitle className="text-lg">{product.name}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {product.image_url && (() => {
                            let imageUrls: string[] = [];
                            try {
                              imageUrls = JSON.parse(product.image_url);
                              if (!Array.isArray(imageUrls)) {
                                imageUrls = [product.image_url];
                              }
                            } catch {
                              imageUrls = [product.image_url];
                            }
                            
                            return imageUrls.length > 1 ? (
                              <Carousel className="w-full">
                                <CarouselContent>
                                  {imageUrls.map((url, index) => (
                                    <CarouselItem key={index}>
                                      <div className="w-full h-96 bg-secondary/20 rounded-md overflow-hidden flex items-center justify-center">
                                        <img
                                          src={`${url}?v=${Date.now()}`}
                                          alt={`${product.name} - Image ${index + 1}`}
                                          className="w-full h-full object-contain"
                                          onError={(e) => {
                                            console.error('Image failed to load:', url);
                                            e.currentTarget.style.display = 'none';
                                          }}
                                        />
                                      </div>
                                    </CarouselItem>
                                  ))}
                                </CarouselContent>
                                <CarouselPrevious className="left-2" />
                                <CarouselNext className="right-2" />
                              </Carousel>
                            ) : (
                              <div className="w-full h-96 bg-secondary/20 rounded-md overflow-hidden flex items-center justify-center">
                                <img
                                  src={`${imageUrls[0]}?v=${Date.now()}`}
                                  alt={product.name}
                                  className="w-full h-full object-contain"
                                  onError={(e) => {
                                    console.error('Image failed to load:', imageUrls[0]);
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              </div>
                            );
                          })()}
                          <p className="text-sm leading-relaxed">{product.description}</p>
                          {product.shopify_product_handle ? (
                            <Button 
                              onClick={() => handleAddToCart(product)}
                              disabled={addingToCart === product.id}
                              className="w-full"
                            >
                              {addingToCart === product.id ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Adding...
                                </>
                              ) : (
                                <>
                                  <ShoppingCart className="mr-2 h-4 w-4" />
                                  Add to Cart
                                </>
                              )}
                            </Button>
                          ) : product.link ? (
                            <Button asChild variant="outline" className="w-full">
                              <a href={product.link} target="_blank" rel="noopener noreferrer">
                                Shop Now →
                              </a>
                            </Button>
                          ) : null}
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Card className="md:col-span-2">
                      <CardContent className="py-12 text-center">
                        <p className="text-muted-foreground">No product recommendations available yet.</p>
                        <p className="text-sm text-muted-foreground mt-2">Check back soon!</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-6 pt-6 pb-2">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="text-foreground/70 hover:text-foreground"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Home
        </Button>
      </div>

      {/* Editorial hero */}
      <header className="container mx-auto px-6 pt-8 pb-14 max-w-4xl">
        <p className="text-xs uppercase tracking-[0.3em] text-primary mb-5">Chapter Two — Explore</p>
        <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] text-foreground mb-6">
          Symptom Explorer
        </h1>
        <p className="text-lg md:text-xl text-foreground/70 leading-relaxed max-w-2xl font-light">
          A calm, plain-language guide to what's happening in your body — with the science,
          quizzes, practical tools, and gentle product suggestions.
        </p>
        <div className="mt-8 h-px w-24 bg-foreground/20" />
      </header>

      <div className="container mx-auto px-6 pb-24 max-w-5xl">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredSymptoms.length === 0 ? (
          <div className="max-w-2xl mx-auto py-16 text-center">
            <p className="text-foreground/70 mb-2 font-serif text-2xl">Nothing found.</p>
            <p className="text-sm text-foreground/60">
              {symptoms.length === 0
                ? "No symptoms have been added yet. Please check back soon."
                : "Try adjusting your search or filter."}
            </p>
          </div>
        ) : categoryFilter === 'all' ? (
          <div>
            <Accordion type="multiple" className="divide-y divide-foreground/10 border-y border-foreground/10" defaultValue={[]}>
              {Object.entries(groupedSymptoms).map(([category, categorySymptoms]) => {
                if (categorySymptoms.length === 0) return null;
                const categoryInfo = CATEGORIES.find(c => c.value === category);
                return (
                  <AccordionItem key={category} value={category} className="border-none">
                    <AccordionTrigger className="hover:no-underline py-7 px-2 group">
                      <div className="flex items-baseline gap-6 text-left w-full">
                        <span className="text-xs uppercase tracking-[0.25em] text-primary/70 tabular-nums w-10">
                          {String(CATEGORIES.slice(1).findIndex(c => c.value === category) + 1).padStart(2, '0')}
                        </span>
                        <div className="flex-1">
                          <h2 className="text-2xl md:text-3xl font-serif text-foreground group-hover:text-primary transition-colors">
                            {categoryInfo?.label}
                          </h2>
                          <p className="text-xs text-foreground/50 mt-1.5 font-light tracking-wide">
                            {categorySymptoms.length} {categorySymptoms.length === 1 ? 'symptom' : 'symptoms'}
                          </p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2 pb-10 px-2">
                      <div className="grid gap-x-10 gap-y-6 md:grid-cols-2 lg:grid-cols-3">
                        {categorySymptoms.map(symptom => (
                          <button
                            key={symptom.id}
                            onClick={() => handleSymptomClick(symptom)}
                            className="text-left group border-t border-foreground/10 pt-5 hover:border-primary transition-colors"
                          >
                            <h3 className="text-xl font-serif text-foreground group-hover:text-primary transition-colors mb-2">
                              {symptom.title}
                            </h3>
                            <p className="text-sm text-foreground/65 leading-relaxed line-clamp-3 font-light">
                              {symptom.excerpt || symptom.science_content.substring(0, 140) + '…'}
                            </p>
                            <span className="mt-3 inline-block text-xs uppercase tracking-[0.2em] text-primary/80">
                              Read →
                            </span>
                          </button>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>

        ) : (
          <div>

            <p className="text-xs uppercase tracking-[0.25em] text-foreground/50 mb-8">
              {CATEGORIES.find(c => c.value === categoryFilter)?.label} · {filteredSymptoms.length} {filteredSymptoms.length !== 1 ? 'symptoms' : 'symptom'}
            </p>
            <div className="grid gap-x-10 gap-y-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredSymptoms.map(symptom => (
                <button
                  key={symptom.id}
                  onClick={() => handleSymptomClick(symptom)}
                  className="text-left group border-t border-foreground/10 pt-5 hover:border-primary transition-colors"
                >
                  <h3 className="text-xl font-serif text-foreground group-hover:text-primary transition-colors mb-2">
                    {symptom.title}
                  </h3>
                  <p className="text-sm text-foreground/65 leading-relaxed line-clamp-3 font-light">
                    {symptom.excerpt || symptom.science_content.substring(0, 140) + '…'}
                  </p>
                  <span className="mt-3 inline-block text-xs uppercase tracking-[0.2em] text-primary/80">
                    Read →
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}